import { cloudRunViewerTf } from "@p0security/integrations-gcloud-shared/cloud-run-iac";
import type { GcloudIntegration } from "@p0security/integrations-gcloud-shared/types";
import type { GoogleDriveAiComponents } from "@p0security/integrations-google-drive-ai-shared/components";
import type { GoogleDriveAiIntegration } from "@p0security/integrations-google-drive-ai-shared/types";
import {
  type FrontendInstallContext,
  type FrontendInstaller,
  integrationOptionProvider,
} from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

export type GoogleDriveAiInstaller = FrontendInstaller<
  typeof GoogleDriveAiComponents
>;

const { Link, Paragraph, Text } = Typography;

/** Pinned by tag and digest, like the other connector images. */
const CONNECTOR_IMAGE =
  "docker.io/p0security/p0-connector-google-drive-ai:sha-841d828@sha256:868d2c6cecd26ca21ddfb717cd1813885dcaf301fc6294984e6321a67354b164";

/** APIs the connector calls.
 *
 * `drive` is the only one the connector itself reaches; `iam` and `run` are
 * what this Terraform needs to create the service account and the service.
 * There is deliberately no `cloudresourcemanager` — unlike every other GCP
 * connector P0 ships, this one holds no project-level IAM role at all.
 *
 * Listed in the Terraform as a prerequisite rather than enabled by it: other
 * resources in the customer's project depend on these APIs too, so their
 * lifecycle is not this connector's to own.
 */
const REQUIRED_SERVICES = [
  "drive.googleapis.com",
  "iam.googleapis.com",
  "run.googleapis.com",
];

/** The connector and the identity it runs as, and nothing else.
 *
 * PLACEHOLDER. Written out resource by resource because the
 * `p0-security/p0-connector/google` module only deploys VPC-bound `cloudsql`
 * connectors today. Replace with a module call once it can deploy this service
 * without VPC egress.
 *
 * No `google_project_iam_member`, and that absence is the design: the
 * connector's authority over a shared drive is Drive-side membership, granted
 * by a human in the Workspace UI one drive at a time. Nothing here can widen
 * it, which is why no Terraform P0 hands a customer ever needs to.
 */
export const googleDriveAiTerraform = (params: {
  projectId: string;
  region: string;
  connectorServiceName: string;
  connectorServiceAccountId: string;
  invokerServiceAccount: string;
}): string => {
  const {
    projectId,
    region,
    connectorServiceName,
    connectorServiceAccountId,
    invokerServiceAccount,
  } = params;
  const services = REQUIRED_SERVICES.map((service) => `#   - ${service}`).join(
    "\n"
  );
  return `
# PLACEHOLDER: replace with the p0-security/p0-connector/google module once it
# supports the google-drive-ai service.
#
# Requires these APIs to be enabled in the project:
${services}

resource "google_service_account" "p0_google_drive_connector" {
  project      = "${projectId}"
  account_id   = "${connectorServiceAccountId}"
  display_name = "P0 Google Drive connector"
  description  = "Runs P0's Google Drive connector. Add it as a Manager on each shared drive P0 grants in."
}

resource "google_cloud_run_v2_service" "p0_google_drive_connector" {
  project             = "${projectId}"
  name                = "${connectorServiceName}"
  location            = "${region}"
  deletion_protection = false
  # P0 invokes the connector over HTTPS from outside GCP; access is gated by
  # roles/run.invoker and the connector's own token check, not by ingress.
  ingress = "INGRESS_TRAFFIC_ALL"

  template {
    service_account = google_service_account.p0_google_drive_connector.email

    containers {
      image = "${CONNECTOR_IMAGE}"

      # The connector checks this against the caller's OIDC token, so it is
      # not redundant with the roles/run.invoker grant.
      env {
        name  = "INVOKER_SA_EMAIL"
        value = "${invokerServiceAccount}"
      }
    }
  }
}

resource "google_cloud_run_v2_service_iam_member" "p0_google_drive_connector_invoker" {
  project  = "${projectId}"
  location = "${region}"
  name     = google_cloud_run_v2_service.p0_google_drive_connector.name
  role     = "roles/run.invoker"
  member   = "serviceAccount:${invokerServiceAccount}"
}

${cloudRunViewerTf({
  resource: "p0_google_drive_connector_viewer",
  project: `"${projectId}"`,
  location: `"${region}"`,
  service: "google_cloud_run_v2_service.p0_google_drive_connector.name",
  member: invokerServiceAccount,
})}
`;
};

const connectorInstructions: GoogleDriveAiInstaller["components"]["connector"]["instructions"] =
  (context, projectId, item): Instructions<ReactNode> => {
    const gcloud = context.installed.gcloud as GcloudIntegration | undefined;
    const invokerServiceAccount =
      gcloud?.root?.[SINGLETON_ITEM]?.serviceAccountEmail ?? "";
    const connectorServiceAccount = item.connectorServiceAccount;
    const connectorServiceAccountId =
      connectorServiceAccount?.split("@")[0] ?? "";

    return {
      commands: {
        iac: [
          {
            header: (
              <>
                <Paragraph>
                  P0&apos;s Google Drive connector runs in this project as its
                  own service account. It holds no project-level role: its only
                  authority is the shared drives a human has made it a member
                  of.
                </Paragraph>
                <Paragraph>
                  P0&apos;s service account{" "}
                  <Text code>{invokerServiceAccount}</Text> needs{" "}
                  <Text code>roles/run.viewer</Text> to look up the
                  connector&apos;s URL.
                </Paragraph>
                <Paragraph>
                  First, ensure the{" "}
                  <Link
                    href={`https://console.cloud.google.com/apis/library/drive.googleapis.com?project=${projectId}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Google Drive API
                  </Link>{" "}
                  is enabled in this project. This is required in order for the
                  connector to manage permissions on shared drives.
                </Paragraph>
                <Paragraph>
                  Add the following to your Terraform, apply it, then click
                  &quot;Next&quot;. Registering the shared drives comes next,
                  and needs{" "}
                  {connectorServiceAccount ? (
                    <Text code copyable>
                      {connectorServiceAccount}
                    </Text>
                  ) : (
                    "the connector's address"
                  )}
                  .
                </Paragraph>
              </>
            ),
            command: googleDriveAiTerraform({
              projectId,
              region: item.connectorRegion,
              connectorServiceName: item.connectorServiceName,
              connectorServiceAccountId,
              invokerServiceAccount,
            }),
          },
        ],
      },
    };
  };

/** The one step of this install P0 cannot do for a customer.
 *
 * Drive memberships are a Workspace-admin privilege, and P0 deliberately does
 * not hold one — so the connector cannot add itself to a drive, and this screen
 * is where a drive manager does it by hand.
 */
const sharedDriveInstructions: GoogleDriveAiInstaller["components"]["shared-drive"]["instructions"] =
  (context, itemId, item): Instructions<ReactNode> => {
    const { config } = context;
    const connector =
      config && item.projectId
        ? installedItem("connector", config, item.projectId)
        : undefined;
    const connectorServiceAccount = connector?.connectorServiceAccount;

    return {
      help: (
        <Paragraph>
          P0 grants in this drive as the connector&apos;s own identity, so that
          identity has to be a member of the drive. Only a drive manager can add
          it.
        </Paragraph>
      ),
      commands: {
        console: (
          <>
            <Paragraph>
              Open the shared drive{" "}
              <Link
                href={`https://drive.google.com/drive/folders/${item.googleDriveId}`}
                target="_blank"
                rel="noreferrer"
              >
                {itemId}
              </Link>
              , choose <Text strong>Manage members</Text>, and add:
            </Paragraph>
            <Paragraph>
              {connectorServiceAccount ? (
                <Text code copyable>
                  {connectorServiceAccount}
                </Text>
              ) : (
                <Text type="warning">
                  The Drive connector has not finished installing, so its
                  address is not known yet. Finish installing it, then return
                  here.
                </Text>
              )}
            </Paragraph>
            <Paragraph>
              Give it the <Text strong>Manager</Text> role — Drive&apos;s lower
              roles cannot write permissions on items in the drive — then click
              &quot;Next&quot;.
            </Paragraph>
          </>
        ),
      },
    };
  };

/** The projects this tenant runs a Drive connector in, labelled as projects:
 * the shared drive's field asks which project to create service accounts in.
 *
 * Read from this integration's own live config rather than re-fetched: the
 * pointer targets a sibling component, not another integration, so
 * `otherInstalledIntegration` would spend a Firestore read on a document the
 * install context already holds.
 */
const projectOptions = async (
  context: FrontendInstallContext<GoogleDriveAiIntegration>
) => {
  const { config } = context;
  if (!config) return [];
  return Object.entries(installedItems("connector", config))
    .map(([id, connector]) => ({
      id,
      label: connector?.label
        ? `Google Cloud project: ${connector.label} (${id})`
        : `Google Cloud project: ${id}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const googleDriveAiInstaller: GoogleDriveAiInstaller = {
  components: {
    connector: {
      // The connector runs in the project P0 mints agentic identities in, so
      // it must be a GCP Service Account Creator project. Each of those was
      // picked from the gcloud iam-write projects, so P0 can also reach Cloud
      // Run there through gcloud's own credential.
      optionProvider: integrationOptionProvider("connector", [
        { component: "iam-write", integration: "gcp-service-account" },
      ]),
      items: {},
      instructions: connectorInstructions,
    },
    "shared-drive": {
      items: {
        projectId: { optionProvider: projectOptions },
      },
      instructions: sharedDriveInstructions,
    },
  },
};
